import { useContext, useMemo, useState } from "react";
import { InboxIcon } from "lucide-react";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Conversations } from "./conversations";
import { ChatContext } from "@/store/chat.store";
import { ConversationType } from "@/constants/chat.constant";
import useAuthService from "@/services/auth.service";
import { useUserGroups } from "@/context/user-groups.context";

/**
 * Props for the ChatHistory component
 */
interface ChatHistoryProps {
  /** Type of conversation to display */
  conversationType: ConversationType;
  /** Whether chat history is loading */
  isLoadingHistory: boolean;
  /** Callback to refetch conversation folders */
  refetchFolders?: () => void;
}

/**
 * HistorySkeleton component displays loading skeleton placeholders for chat history.
 * Shows 6 skeleton items while history is loading.
 * 
 * @component
 * @returns {JSX.Element} Skeleton loading placeholders
 */
const HistorySkeleton = () => {
  return (
    <>
      {[...Array(6)].map((_, i) => (
        <SidebarMenuItem key={i}>
          <div className="h-9 bg-gray-200 rounded-md animate-pulse mx-2" />
        </SidebarMenuItem>
      ))}
    </>
  );
};

/**
 * ChatHistory component displays a scrollable list of conversation history for
 * a specific conversation type. Shows "Show All" button when there are more than
 * 10 conversations. Displays loading skeleton while fetching.
 * 
 * @component
 * @param {ChatHistoryProps} props - Component props
 * @param {ConversationType} props.conversationType - Conversation type to display
 * @param {boolean} props.isLoadingHistory - Whether history is loading
 * @param {() => void} [props.refetchFolders] - Refetch folders callback
 * @returns {JSX.Element} Chat history list with conversations
 * 
 * @example
 * ```tsx
 * <ChatHistory
 *   conversationType={ConversationType.STANDARD}
 *   isLoadingHistory={isLoading}
 *   refetchFolders={handleRefetch}
 * />
 * ```
 */
export function ChatHistory({
  conversationType,
  isLoadingHistory,
  refetchFolders,
}: ChatHistoryProps) {
  const { state } = useContext(ChatContext);
  const [showAll, setShowAll] = useState(false);
  const { getCurrentUserIsAdmin } = useAuthService()
  const { isMobile } = useSidebar()

  const isAdmin = getCurrentUserIsAdmin();
  const { canAccessChopGpt, canAccessPolicyAssistant } = useUserGroups();

  // Mirror the same tab-visibility logic as navigation-menu so height is always accurate
  const visibleTabCount = useMemo(() => {
    const env = import.meta.env.VITE_ENV;
    if (env === 'dev' || env === 'uat') return 2; // developers always see both tabs
    return [canAccessChopGpt, canAccessPolicyAssistant].filter(Boolean).length;
  }, [canAccessChopGpt, canAccessPolicyAssistant]);

  const conversationsByType = useMemo(() => {
    const filtered = state.conversations.filter(
      (conv) => conv.conversation_type === conversationType
    );
    return filtered;
  }, [state.conversations, conversationType]);

  const visibleConversations = useMemo(
    () => (showAll ? conversationsByType : conversationsByType.slice(0, 8)),
    [showAll, conversationsByType]
  );

  const chatsLabel =
    conversationType === ConversationType.CHOP_GPT
      ? "CHOP GPT"
      : conversationType === ConversationType.POLICY_ASSISTANT
      ? "Policy Assistant"
        : "Payor Policy";
  
  const getHeightSpan = () => {
    const hasMultipleTabs = visibleTabCount > 1;
    if (isAdmin) {
      if (isMobile) {
        return hasMultipleTabs ? 'max-h-[calc(100svh-280px)]' : 'max-h-[calc(100svh-260px)]';
      } else {
        return hasMultipleTabs ? 'max-h-[calc(100svh-400px)]' : 'max-h-[calc(100svh-360px)]';
      }
    } else {
      return hasMultipleTabs ? 'max-h-[calc(100svh-340px)]' : 'max-h-[calc(100svh-309px)]';
    }
  }

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="text-text-200 text-xs">
        {chatsLabel} - Chats
      </SidebarGroupLabel>

      <SidebarGroupContent>
        <SidebarMenu className={`overflow-y-auto ${getHeightSpan()}`}>
          {isLoadingHistory ? (
            <HistorySkeleton />
          ) : (
            <>
              <Conversations
                conversations={visibleConversations}
                conversationType={conversationType}
                refetchFolders={refetchFolders}
              />

              {conversationsByType.length > 8 && (
                <SidebarMenuItem key="All chats">
                  <SidebarMenuButton asChild>
                    <button
                      type="button"
                      onClick={() => setShowAll((prev) => !prev)}
                      className="flex items-center gap-2 w-full text-left"
                    >
                      <InboxIcon />
                      <span>{showAll ? "Show Less" : "All Chats"}</span>
                    </button>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </>
          )}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}